import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, REQUEST, inject } from '@angular/core';
import {
  PartialMatchRouteSnapshot,
  Route,
  Router,
  UrlTree,
} from '@angular/router';
import {
  DEFAULT_LANGUAGE,
  acceptedLanguageTags,
  negotiateLanguage,
} from './helpers/language-negotiation';
import { WebsiteLanguage } from './i18n';

/**
 * The visitor's language: from the request headers when rendering on the
 * server, from the browser's own preference list otherwise. Runs in an
 * injection context (route matchers and redirect functions both do).
 */
function preferredLanguage(): WebsiteLanguage {
  const request = inject(REQUEST);
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  if (request) {
    return negotiateLanguage(
      acceptedLanguageTags(request.headers.get('accept-language')),
    );
  }

  return isBrowser ? negotiateLanguage(navigator.languages) : DEFAULT_LANGUAGE;
}

/**
 * `/blog` -> `/en/blog`, and `/nope` -> `/en/nope`. Locale-less URLs are what
 * people type, paste and link to, so they resolve to the visitor's language;
 * a path the site does not answer to then lands on that language branch's own
 * 404, which renders inside the branch's providers (the translation token
 * lives there, a 404 rendered at the root would have none). The micro-sites
 * stay locale-less because their routes sit in front of this wildcard.
 *
 * This is the SPA half of the feature (in-app navigation, and any request the
 * Express layer in [`server.ts`](../../../../server.ts) did not answer first).
 * On a server render the SSR engine turns the router's redirect into a real
 * HTTP 302, so a direct hit is not silently rewritten either.
 */
export const LOCALE_LESS_REDIRECT: Route = {
  path: '**',
  redirectTo: localisedUrl,
};

/** `/` -> `/en`, which the language branch then takes to its homepage. */
export const ROOT_LANGUAGE_REDIRECT: Route = {
  path: '',
  pathMatch: 'full',
  redirectTo: localisedUrl,
};

/**
 * The matched URL with the visitor's language in front. A `UrlTree` rather
 * than a string: a string redirect is re-parsed, which drops the query string
 * and fragment the visitor arrived with.
 */
function localisedUrl({
  url,
  queryParams,
  fragment,
}: PartialMatchRouteSnapshot): UrlTree {
  return inject(Router).createUrlTree(
    [preferredLanguage(), ...url.map(segment => segment.path)],
    { queryParams, fragment: fragment ?? undefined },
  );
}
